import { NetworkStatuses } from '@strata/constants';
import { formatPhoneNumber } from '@strata/helper-functions';
import { FormSectionWithMessage, FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import { AddressDetailLabelValue, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { FIcon } from '@strata/react-status-icons';
import { type ReferralStatusIndicator } from '@strata/react-status-indicators';
import { LiteSupplierTag } from '@strata/react-tags';

import { ReferralStatusMessageMap } from 'services/referralHelpers';
import { type SupplierDetails } from 'store/api/referenceApi';

interface PharmacyDetailsProps {
    supplierDetails?: SupplierDetails;
    isLoading?: boolean;
    referralStatus?: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
}

export const PharmacyDetails = ({
    supplierDetails = undefined,
    isLoading = true,
    referralStatus = undefined,
}: PharmacyDetailsProps) => {
    const referralStatusMessage = referralStatus
        ? ReferralStatusMessageMap[referralStatus]
        : undefined;

    if (referralStatusMessage) {
        return (
            <FormSectionWithMessage
                sectionTitle="Pharmacy Details"
                icon={<FIcon />}
                primaryText={referralStatusMessage}
                secondaryText="No details to show."
            />
        );
    }
    return (
        <FormSectionWithRows
            sectionTitle="Pharmacy Details"
            isLoading={isLoading}
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Pharmacy"
                        value={
                            supplierDetails?.name && (
                                <div className="flex__gap--small" style={{ alignItems: 'center' }}>
                                    {supplierDetails.name}
                                    <LiteSupplierTag
                                        lite={
                                            supplierDetails.networkStatus === NetworkStatuses.LITE
                                        }
                                    />
                                </div>
                            )
                        }
                    />
                    <DetailLabelValuePair
                        label="Phone"
                        value={formatPhoneNumber(supplierDetails?.phone)}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Fax"
                        value={formatPhoneNumber(supplierDetails?.fax)}
                    />
                    <DetailLabelValuePair
                        label="Email"
                        value={<MailToHyperlink emailAddress={supplierDetails?.email} />}
                    />
                </>
            }
            rowThreeContent={
                <AddressDetailLabelValue
                    label="Pharmacy Address"
                    address1={supplierDetails?.address1}
                    address2={supplierDetails?.address2}
                    city={supplierDetails?.city}
                    state={supplierDetails?.state}
                    zip={supplierDetails?.zip}
                />
            }
        />
    );
};
